<script setup lang="ts">
/**
 * "กล่องร่าง" (round 9, owner: "แยกกล่องพิมพ์กับกล่องร่าง … AI จะร่างให้เมื่อกดที่กล่องร่าง"): the top band of the composer
 * card. Quiet row "ให้ AI ร่างคำตอบ" until the staff press it (or the owner's "ร่างอัตโนมัติ" already made a draft) →
 * "AI กำลังร่างคำตอบ" → the draft, read only, with "ใช้ร่างนี้" / "ร่างใหม่" / "ปิด". The typing box below stays the
 * staff's own; "ใช้ร่างนี้" is what moves the draft in (the composer does it: `use`).
 * "สคริปต์แนะนำรายคน" (owner 17 ต.ค. 69): when the chat has a suggestion (stores/script.ts) the box shows it first —
 * SuggestionCard, "ใส่ในช่องพิมพ์" = `insert` — and today's AI draft is its "ปรับตามแนวลูกค้า" (the suggestion re-toned for
 * the customer's style; when a number or link would get lost the API gives the owner's text back and the box says so).
 * No suggestion = as before.
 * Every suggestion is the owner's own answer (owner 17 ต.ค. 69): question types without one switched on are named above
 * the quiet row ("ยังไม่ได้กำหนดคำตอบเรื่อง …", UndefinedAnswers) — never a text to insert.
 * คู่มือปิดการขาย (owner 18 ต.ค. 69): the customer waits and nothing is suggested (a reply the playbook has no answer for)
 * → the step card (StepCard: the reply, the step's question, "ให้ AI พาไปขั้นต่อไป") above the quiet row — never empty.
 * The AI's next-step draft shows like the others, chipped "AI พาไปขั้นต่อไป · <ขั้น>".
 */
import { computed, ref, watch } from 'vue'
import type { MediaAsset } from '../../api/types'
import { styleText } from '../../lib/script'
import { NUMBER_MARK, useInbox } from '../../stores/inbox'
import { sendMediaOf, useLibrary } from '../../stores/library'
import { useScript } from '../../stores/script'
import { draftSegments } from '../knowledge/labels'
import MediaThumb from '../library/MediaThumb.vue'
import Icon from '../ui/Icon.vue'
import StepCard from './StepCard.vue'
import SuggestionCard from './SuggestionCard.vue'
import UndefinedAnswers from './UndefinedAnswers.vue'

const props = defineProps<{ convId: string }>()
const emit = defineEmits<{ use: [mode: 'replace' | 'append']; insert: [mode: 'replace' | 'append'] }>()
const store = useInbox()
const lib = useLibrary()
const script = useScript()
watch(
  () => props.convId,
  (id) => script.ensure(id),
  { immediate: true },
)

const phase = computed(() => store.draftPhase(props.convId))
// a draft asked for anywhere (Tab, the side panel's "กดให้ AI ร่าง", the owner's auto-draft) shows: the band opens
watch(phase, (p, was) => {
  if ((p === 'generating' || p === 'ready') && was !== p && !script.suggestOff) script.openBand(props.convId)
})
/** The "แนะนำให้ตอบ" card instead of today's box. */
const showScript = computed(() => script.showing(props.convId))
/** "ปรับตามแนวลูกค้า" = today's AI draft: not when the AI is off / nothing to answer. */
const canRephrase = computed(() => phase.value !== 'hidden' && phase.value !== 'disabled')
/** The suggestion was skipped / used / swapped for the AI draft: one tap brings it back from the quiet row. */
const back = computed<'skipped' | 'used' | 'ai' | null>(() => {
  const h = script.hidden(props.convId)
  if (h) return h
  const u = script.ui[props.convId]
  return u?.aiView && phase.value === 'collapsed' && script.current(props.convId) ? 'ai' : null
})
/** The answer types the owner has not defined for this chat's questions (nothing suggested for them). */
const undef = computed(() => script.undefinedOf(props.convId))
const quietPhase = computed(() => phase.value === 'collapsed' || phase.value === 'disabled' || phase.value === 'hidden')
/** คู่มือปิดการขาย: nothing suggested for the waiting customer — the step now + "ให้ AI พาไปขั้นต่อไป". */
const stepCard = computed(() => (!showScript.value && quietPhase.value ? script.stepCard(props.convId) : null))
/** A customer wrote after the script was made: it is hidden until the new one (or "อัปเดตคำแนะนำ"). */
const outdated = computed(() => !!script.outdated[props.convId] && !!script.scripts[props.convId] && script.eligible(props.convId))
const box = computed(() => store.draftBoxes[props.convId] ?? null)
const ai = computed(() => store.details[props.convId]?.ai ?? null)
const draft = computed(() => ai.value?.draft ?? '')
const typed = computed(() => (store.composers[props.convId]?.text ?? '').trim() !== '')
/** Words of the staff's own in the typing box (an untouched draft taken before is simply replaced, no question). */
const ownWords = computed(() => {
  const st = store.composers[props.convId]
  return typed.value && !(st?.origin === 'ai' && !st.edited)
})

/** Jev matched a fixed answer: the draft is that library answer ("คำตอบสำเร็จรูป /menu"). */
const quick = computed(() => (ai.value?.draft_source === 'canned' && ai.value.quick ? ai.value.quick : null))
/** The customer asked for the menu list: built by the server from the approved "เมนูรอบนี้". */
const menuList = computed(() => ai.value?.draft_source === 'menu_round')
const menuSub = computed(() => {
  const n = ai.value?.menu_round?.dish_count
  return `ระบบจัดให้${n ? ` · ${n} เมนู` : ''}`
})
const needsCheck = computed(() => draft.value.includes(NUMBER_MARK))
/** "ปรับตามแนวลูกค้า" pressed: the AI is re-toning the suggestion (the busy row says so). */
const adapting = computed(() => !!script.ui[props.convId]?.aiView && !!script.ui[props.convId]?.adapt)
/** "ให้ AI พาไปขั้นต่อไป" pressed (the busy row says so). */
const leading = computed(() => !!script.ui[props.convId]?.aiView && !!script.ui[props.convId]?.next)
/** The draft is the AI's next-step message (`fallback` = it asked 2+ questions: the step's own question instead). */
const nextStep = computed(() => ai.value?.next_step ?? null)
/** The draft is the suggestion re-toned for the customer's style (`fallback` = the owner's own text came back). */
const adapted = computed(() => ai.value?.adapt ?? null)
const segments = computed(() => draftSegments(draft.value))

// the files that go out with this draft: the quick answer's pictures, or `ai.media` (the round's poster)
watch(
  () => [phase.value, quick.value?.canned_id] as const,
  ([p, id]) => {
    if (p === 'ready' && id && !lib.findCanned(id, props.convId)) void lib.loadPicker(props.convId)
  },
  { immediate: true },
)
const media = computed<MediaAsset[]>(() => {
  const q = quick.value
  if (q) {
    const item = lib.findCanned(q.canned_id, props.convId)
    return item ? sendMediaOf(item) : []
  }
  return ai.value?.media ?? []
})

/** Why nothing is drafted: the AI's own reason, or a plain line. */
const noneText = computed(() => ai.value?.no_draft_reason?.trim() || 'AI ไม่มีร่างสำหรับข้อความล่าสุดของลูกค้า')

// "ใช้ร่างนี้" while the staff typed something: ask inline (no modal) — replace, add after, or keep typing
const confirming = ref(false)
watch(
  () => [props.convId, phase.value] as const,
  () => (confirming.value = false),
)

function use(): void {
  if (ownWords.value) {
    confirming.value = true
    return
  }
  emit('use', 'replace')
}

function pick(mode: 'replace' | 'append'): void {
  confirming.value = false
  emit('use', mode)
}

function open(): void {
  if (phase.value === 'collapsed') void store.openDraft(props.convId)
}

function redraft(): void {
  confirming.value = false
  // the draft of "ปรับตามแนวลูกค้า" / "เรียบเรียงใหม่": again from the same suggestion
  const u = script.ui[props.convId]
  if (u?.aiView && u.next) void script.nextStep(props.convId, { refresh: true })
  else if (u?.aiView) void (u.adapt ? script.adapt(props.convId, { refresh: true }) : script.rephrase(props.convId, { refresh: true }))
  else void store.requestDraft(props.convId, { refresh: true })
}

/** "ปิด" on the AI draft: back to the quiet row — or to the suggestion it was opened from ("ปรับตามแนวลูกค้า"). */
function close(): void {
  store.closeDraft(props.convId)
  if (script.ui[props.convId]?.aiView) script.reshow(props.convId)
}
</script>

<template>
  <!-- "ปิดคำแนะนำ" (this staff member, stores/script.ts suggestOff): one quiet line to bring it back -->
  <div v-if="script.suggestOff" class="dbx dbx-off">
    <div class="dbx-rw">
      <span class="dbx-off-t"><Icon name="sparkles" />ปิดคำแนะนำอยู่ · ขายเอง</span>
      <button type="button" class="dbx-off-b" title="เปิดกล่องแนะนำให้ตอบอีกครั้ง (เฉพาะคุณ)" @click="script.setSuggestOff(false)">
        เปิดคำแนะนำ
      </button>
    </div>
  </div>
  <div
    v-else-if="script.bandOpen === convId && (phase !== 'hidden' || showScript || back || outdated || undef.length || stepCard)"
    class="dbx"
    :class="showScript ? 'is-script' : `is-${phase}`"
  >
    <!-- the owner has not defined the answer for these questions yet: said, never written for them -->
    <UndefinedAnswers v-if="!showScript && quietPhase && !outdated && undef.length" :topics="undef" />
    <!-- คู่มือปิดการขาย: nothing suggested for the waiting customer — the step now + "ให้ AI พาไปขั้นต่อไป" -->
    <StepCard v-if="stepCard" :conv-id="convId" :card="stepCard" :can-ai="canRephrase" />
    <!-- "แนะนำให้ตอบ": the personal script (stores/script.ts) comes before today's AI draft -->
    <SuggestionCard v-if="showScript" :conv-id="convId" :can-rephrase="canRephrase" @insert="(m) => emit('insert', m)" />

    <!-- quiet row: nothing asked yet · used · nothing to answer yet (+ bring the suggestion back) -->
    <div v-else-if="phase === 'collapsed' || phase === 'disabled' || phase === 'hidden'" class="dbx-rw">
      <button
        v-if="phase !== 'hidden'"
        type="button"
        class="dbx-row"
        :disabled="phase === 'disabled'"
        :aria-label="phase === 'disabled' ? 'ให้ AI ร่างคำตอบ (ยังไม่มีข้อความลูกค้าให้ตอบ)' : 'ให้ AI ร่างคำตอบ'"
        @click="open"
      >
        <Icon name="sparkles" />
        <span class="dbx-l">ให้ AI ร่างคำตอบ</span>
        <span v-if="phase === 'disabled'" class="dbx-m">ยังไม่มีข้อความลูกค้าให้ตอบ</span>
        <span v-else-if="box?.used" class="dbx-m used"><Icon name="check" />ใช้ร่างแล้ว</span>
        <span v-else-if="back === 'used'" class="dbx-m used"><Icon name="check" />ใส่คำแนะนำแล้ว</span>
        <span class="grow"></span>
        <span v-if="phase === 'collapsed' && !typed && !back && !outdated" class="kbd-hint">กด <kbd>Tab</kbd></span>
      </button>
      <span v-else class="grow"></span>
      <button v-if="outdated" type="button" class="dbx-sg" :disabled="!!script.loading[convId]" @click="script.refresh(convId)">
        <Icon name="refresh" />{{ script.loading[convId] ? 'กำลังอัปเดต…' : 'อัปเดตคำแนะนำ' }}
      </button>
      <button v-else-if="back" type="button" class="dbx-sg" @click="script.reshow(convId)"><Icon name="sparkles" />ดูคำแนะนำ</button>
    </div>

    <!-- the AI is writing (same soft dots as the AI-mode "กำลังคิด" row, never a skeleton) -->
    <div v-else-if="phase === 'generating'" class="dbx-row busy" role="status" aria-live="polite">
      <Icon name="sparkles" />
      <span class="dbx-l">{{ adapting ? 'AI กำลังปรับตามแนวลูกค้า' : leading ? 'AI กำลังพาไปขั้นต่อไป' : 'AI กำลังร่างคำตอบ' }}</span>
      <span class="dots" aria-hidden="true"><i></i><i></i><i></i></span>
      <span class="grow"></span>
      <span class="dbx-m">ใช้เวลาไม่กี่วินาที</span>
    </div>

    <!-- the draft -->
    <div v-else-if="phase === 'ready'" class="dbx-card" role="region" aria-label="กล่องร่าง">
      <div class="dbx-h">
        <span v-if="quick" class="ai-chip quick"><Icon name="zap" />คำตอบสำเร็จรูป <span class="mono">/{{ quick.shortcut }}</span></span>
        <span v-else-if="menuList" class="ai-chip quick menu"><Icon name="utensils" />เมนูรอบนี้ (อนุมัติแล้ว)</span>
        <span v-else-if="nextStep" class="ai-chip"><Icon name="sparkles" />AI พาไปขั้นต่อไป</span>
        <span v-else-if="adapted && !adapted.fallback" class="ai-chip"><Icon name="sparkles" />ปรับตามแนวลูกค้า</span>
        <span v-else-if="adapted" class="ai-chip quick"><Icon name="sparkles" />ข้อความเดิมของคำแนะนำ</span>
        <span v-else class="ai-chip"><Icon name="sparkles" />ร่างจาก AI</span>
        <span class="dbx-sub">{{
          quick
            ? 'Jev เลือกจากคลังคำตอบ'
            : menuList
              ? menuSub
              : nextStep
                ? `${nextStep.label ? `ขั้น${nextStep.label} · ` : ''}ตรวจก่อนใช้ แก้ต่อได้ในช่องพิมพ์`
                : adapted && !adapted.fallback
                ? `${styleText(adapted)} · ตรวจก่อนใช้`
                : 'ตรวจก่อนใช้ แก้ต่อได้ในช่องพิมพ์'
        }}</span>
      </div>
      <div class="dbx-t" :class="{ tall: menuList }" tabindex="0" aria-label="ข้อความร่าง">
        <template v-for="(s, i) in segments" :key="i"
          ><mark v-if="s.mark" class="dbx-mark">{{ s.text }}</mark
          ><template v-else>{{ s.text }}</template></template
        >
      </div>
      <p v-if="nextStep?.fallback" class="dbx-note" role="note">
        <Icon name="info" />AI ถามลูกค้าเกิน 1 คำถาม จึงใช้คำถามของขั้นนี้แทน
      </p>
      <p v-if="adapted?.fallback" class="dbx-note" role="note">
        <Icon name="info" />ข้อมูลที่ปรับไม่ตรงกับคำแนะนำ (ตัวเลขหรือลิงก์) จึงใช้ข้อความเดิมของคำแนะนำแทน
      </p>
      <p v-if="needsCheck" class="dbx-warn"><Icon name="alertCircle" />มี [เช็คตัวเลข] ใส่ตัวเลขที่ถูกต้องก่อนส่ง</p>
      <div v-if="media.length" class="dbx-media" aria-label="รูปที่ส่งไปพร้อมร่างนี้">
        <span v-for="m in media" :key="m.id" class="dbx-th" :title="m.title"><MediaThumb :asset="m" :size="44" /></span>
        <span class="dbx-mn">{{ media.length > 1 ? `ส่ง ${media.length} รายการนี้ไปด้วย` : 'ส่งรูปนี้ไปด้วย' }}</span>
      </div>
      <div v-if="confirming" class="dbx-a ask" role="group" aria-label="ใช้ร่างนี้กับข้อความที่พิมพ์ไว้">
        <span class="dbx-q">แทนข้อความที่พิมพ์ไว้?</span>
        <button type="button" class="dbx-b pri" @click="pick('replace')">แทนที่</button>
        <button type="button" class="dbx-b" @click="pick('append')">ต่อท้าย</button>
        <button type="button" class="dbx-b ghost" @click="confirming = false">ยกเลิก</button>
      </div>
      <div v-else class="dbx-a">
        <button type="button" class="dbx-b pri" @click="use"><Icon name="check" />ใช้ร่างนี้</button>
        <button type="button" class="dbx-b" @click="redraft"><Icon name="refresh" />ร่างใหม่</button>
        <span class="grow"></span>
        <span v-if="!typed" class="kbd-hint">กด <kbd>Tab</kbd> เพื่อใช้</span>
        <button type="button" class="dbx-b ghost" @click="close">ปิด</button>
      </div>
    </div>

    <!-- no draft (the AI chose not to write one) · the request failed -->
    <div v-else class="dbx-card line" role="note">
      <span class="dbx-why" :class="{ err: phase === 'error' }">
        <Icon :name="phase === 'error' ? 'alertCircle' : 'info'" />
        <span>{{ phase === 'error' ? box?.error : noneText }}</span>
      </span>
      <span class="grow"></span>
      <button type="button" class="dbx-b" @click="redraft"><Icon name="refresh" />{{ phase === 'error' ? 'ลองใหม่' : 'ร่างใหม่' }}</button>
      <button type="button" class="dbx-b ghost" @click="close">ปิด</button>
    </div>
  </div>
</template>

<style scoped>
.dbx {
  border-bottom: 1px solid #e3ece7;
  background: #f8fbf9;
  border-radius: 11px 11px 0 0;
}
.grow {
  flex: 1;
}
/* ---- suggestions switched off (this staff member) */
.dbx-off .dbx-rw {
  justify-content: space-between;
  gap: 8px;
  padding: 0 8px 0 12px;
}
.dbx-off-t {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  line-height: 18px;
  color: var(--mut);
}
.dbx-off-t :deep(.ic) {
  width: 14px;
  height: 14px;
}
.dbx-off-b {
  height: 28px;
  padding: 0 10px;
  border: 1px solid #b9dccb;
  border-radius: 8px;
  background: #fff;
  color: var(--ac2);
  font-size: 12.5px;
  font-weight: 600;
}
.dbx-off-b:hover {
  background: #f1f8f4;
}
/* ---- quiet row */
.dbx-rw {
  display: flex;
  align-items: center;
  min-height: 38px;
}
.dbx-sg {
  flex: none;
  height: 30px;
  margin-right: 6px;
  padding: 0 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  color: var(--ac2);
  white-space: nowrap;
}
.dbx-sg:hover:not(:disabled) {
  background: var(--acs);
}
.dbx-sg:disabled {
  color: var(--mut);
}
.dbx-sg :deep(.ic) {
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.dbx-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  min-height: 38px;
  padding: 7px 12px;
  border-radius: 11px 11px 0 0;
  background: transparent;
  text-align: left;
  color: var(--ac2);
}
button.dbx-row:hover:not(:disabled) {
  background: #eff7f2;
}
button.dbx-row:focus-visible {
  outline: 2px solid #9fcdb8;
  outline-offset: -2px;
}
.dbx-row:disabled {
  color: #8a9a92;
  cursor: default;
}
.dbx-row > :deep(.ic) {
  flex: none;
  width: 15px;
  height: 15px;
  color: var(--ac);
}
.dbx-row:disabled > :deep(.ic) {
  color: #a9b8b1;
}
.dbx-l {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.dbx-m {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  font-weight: 400;
}
.dbx-m.used {
  color: #5d7a6c;
}
.dbx-m :deep(.ic) {
  width: 13px;
  height: 13px;
  color: var(--ac);
}
.dbx-row.busy {
  color: #2d5a4b;
}
.dbx-row.busy .dbx-l {
  font-weight: 500;
}
.dots {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: -2px;
}
.dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--ac);
  opacity: 0.3;
  animation: dbx-dot 1.2s ease-in-out infinite;
}
.dots i:nth-child(2) {
  animation-delay: 0.2s;
}
.dots i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes dbx-dot {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .dots i {
    animation: none;
    opacity: 0.6;
  }
}
/* ---- the draft */
.dbx-card {
  padding: 10px 12px;
}
.dbx-card.line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  padding: 7px 8px 7px 12px;
}
.dbx-h {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-width: 0;
}
.ai-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px 0 6px;
  border-radius: 6px;
  background: var(--acs);
  color: var(--ac2);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.ai-chip :deep(.ic) {
  width: 14px;
  height: 14px;
}
.ai-chip.quick {
  background: #eef2f0;
  color: var(--ink);
}
.ai-chip.quick .mono {
  margin-left: 2px;
  color: var(--ac2);
}
.ai-chip.quick :deep(.ic) {
  color: var(--ac);
}
.dbx-sub {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.dbx-t {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #dcebe3;
  border-left: 3px solid #7fbfa2;
  background: #fff;
  font-size: 14px;
  line-height: 22px;
  color: #33423b;
  white-space: pre-wrap; /* the draft's paragraphs, as the customer will see them */
  overflow-wrap: anywhere;
  max-height: min(232px, 32vh); /* a draft in paragraphs (blank lines between) whole */
  overflow-y: auto;
}
.dbx-t.tall {
  max-height: max(232px, min(264px, 30vh));
}
.dbx-t:focus-visible {
  outline: 2px solid #9fcdb8;
  outline-offset: 1px;
}
.dbx-mark {
  padding: 0 3px;
  border-radius: 4px;
  background: #fdf0e1;
  color: #a4540b;
  font-weight: 600;
}
.dbx-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.dbx-note :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  color: #8fa39a;
}
.dbx-warn {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: #a4540b;
  font-weight: 600;
}
.dbx-warn :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
}
.dbx-media {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.dbx-th {
  display: inline-flex;
  border-radius: 9px;
  border: 1px solid var(--line);
  padding: 2px;
  background: #fff;
}
.dbx-mn {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.dbx-a {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-top: 10px;
}
.dbx-q {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ink);
  margin-right: 4px;
}
.dbx-b {
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #cfdcd5;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: #2e3b35;
  white-space: nowrap;
}
.dbx-b:hover {
  background: #f4f7f5;
}
.dbx-b :deep(.ic) {
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.dbx-b.pri {
  border-color: var(--ac);
  background: var(--ac);
  color: #fff;
  font-weight: 600;
}
.dbx-b.pri:hover {
  background: var(--ac2);
  border-color: var(--ac2);
}
.dbx-b.pri :deep(.ic) {
  color: #fff;
}
.dbx-b.ghost {
  border-color: transparent;
  background: transparent;
  color: var(--mut);
  padding: 0 10px;
}
.dbx-b.ghost:hover {
  background: #eef2f0;
  color: var(--ink);
}
.dbx-why {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  flex: 1 1 240px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.dbx-why :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2.5px;
  color: #8fa39a;
}
.dbx-why.err :deep(.ic) {
  color: #c07a2c;
}
.kbd-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
kbd {
  font: 600 12px/18px var(--mono);
  padding: 0 6px;
  border-radius: 5px;
  background: #fff;
  border: 1px solid #d3ddd8;
  border-bottom-width: 2px;
  color: var(--ink);
}
</style>
